import React, { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import logo from "../../assets/usjp-logo__1_-removebg-preview.png";
import { useAuth } from "../../hooks/useAuth";
import { ADMIN_NAV, ADMIN_NAV_GROUPS } from "../../config/adminNav";
import { TOKENS, PAGE_BG, APP_FONT, Icon, ICONS, Avatar } from "../../components/common/DashboardUI";
import {
  getUnreadNotificationCount,
  listNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from "../../services/notificationService";

// How often to poll for a fresh unread count while the panel is closed.
const NOTIF_POLL_MS = 30000;

export default function AdminLayout() {
  const { user, role, can, isSuperAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  // Manually-toggled-open nav groups (e.g. "renewals") — a group also shows
  // open whenever the current route is one of its children, regardless of
  // this set (see isChildActive below), so it can't be collapsed out from
  // under the page you're actually on.
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const toggleGroup = (key) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // ── Notifications (generic in-app feed — rows are scoped server-side to the
  // logged-in system user, so this "just works" for Bursar without any role
  // check here; other roles simply see an empty list until something targets
  // their user id). ──────────────────────────────────────────────────────
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifRows, setNotifRows] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [markingAll, setMarkingAll] = useState(false);

  const refreshUnreadCount = useCallback(() => {
    getUnreadNotificationCount()
      .then(setUnreadCount)
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshUnreadCount();
    const timer = setInterval(refreshUnreadCount, NOTIF_POLL_MS);
    return () => clearInterval(timer);
  }, [refreshUnreadCount]);

  const openNotifications = () => {
    setNotifOpen((o) => !o);
    if (!notifOpen) {
      setNotifLoading(true);
      listNotifications({ per_page: 10 })
        .then((data) => {
          setNotifRows(data?.rows || []);
          setUnreadCount(data?.unread_count ?? 0);
        })
        .catch(() => toast.error("Couldn't load notifications."))
        .finally(() => setNotifLoading(false));
    }
  };

  const handleMarkRead = (id) => {
    setNotifRows((rows) => rows.map((r) => (r.id === id ? { ...r, read_at: new Date().toISOString() } : r)));
    markNotificationRead(id)
      .then((r) => setUnreadCount(r?.unread_count ?? 0))
      .catch(() => refreshUnreadCount());
  };

  const handleMarkAllRead = () => {
    setMarkingAll(true);
    markAllNotificationsRead()
      .then(() => {
        setNotifRows((rows) => rows.map((r) => ({ ...r, read_at: r.read_at || new Date().toISOString() })));
        setUnreadCount(0);
      })
      .catch(() => toast.error("Couldn't mark notifications as read."))
      .finally(() => setMarkingAll(false));
  };

  // Sidebar is generated straight from the same module registry that drives
  // routing (ADMIN_NAV) — an entry shows only if the logged-in user has its
  // "view_*" permission, or (for the rare role-gated module) is Super Admin.
  // Action permissions never appear here; they gate buttons inside each page
  // instead.
  // The Shroff role gets no "Dashboard" sidebar link (sidebar only — the
  // /admin index route itself is unchanged).
  const isShroff = (user?.roles ?? []).some((r) => String(r).toLowerCase() === "shroff");
  const items = ADMIN_NAV.filter((item) =>
    item.key === "dashboard" && isShroff ? false : item.requireSuperAdmin ? isSuperAdmin : can(item.permission)
  );

  // Fold consecutive `items` sharing a `group` key (see ADMIN_NAV_GROUPS)
  // into one row with a `children` array, so they render as a single
  // collapsible parent. A group left with only one visible child (a role
  // missing one of the two renewal permissions, say) renders as that
  // child's own flat link instead of a pointless one-item dropdown.
  const sidebarRows = [];
  const groupRowIndex = new Map();
  items.forEach((item) => {
    if (!item.group) {
      sidebarRows.push({ item });
      return;
    }
    if (groupRowIndex.has(item.group)) {
      sidebarRows[groupRowIndex.get(item.group)].children.push(item);
      return;
    }
    groupRowIndex.set(item.group, sidebarRows.length);
    sidebarRows.push({ group: item.group, children: [item] });
  });

  // Current page title for the header (longest matching path wins so /admin/users
  // beats /admin).
  const current =
    [...ADMIN_NAV].sort((a, b) => b.to.length - a.to.length).find((n) =>
      n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)
    ) || ADMIN_NAV[0];

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out.");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className={`min-h-screen flex overflow-x-hidden ${PAGE_BG} m-0`} style={{ fontFamily: APP_FONT }}>
      {/* ══ SIDEBAR ══ */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-64 flex flex-col transition-transform ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        style={{ background: `linear-gradient(180deg, ${TOKENS.SIDEBAR_TOP} 0%, ${TOKENS.SIDEBAR_BOTTOM} 100%)` }}
      >
        <div className="flex items-center gap-3 px-5 h-16 border-b border-white/10">
          <div className="w-9 h-9 rounded-full flex items-center justify-center bg-white">
            <img src={logo} alt="USJ" className="w-7 h-7 object-contain" />
          </div>
          <div className="leading-tight">
            <p className="text-white font-bold text-sm">USJ SPORTS</p>
            <p className="text-[10px] text-blue-200">Admin Portal</p>
          </div>
        </div>

        {/* Operator card */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
          <Avatar name={user?.name} size={40} />
          <div className="min-w-0">
            <p className="text-white text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-[11px] truncate text-blue-200">{role}</p>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {sidebarRows.map((row) => {
            if (row.item || row.children.length === 1) {
              const item = row.item || row.children[0];
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setSidebarOpen(false)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
                  style={({ isActive }) => ({
                    backgroundColor: isActive ? "rgba(255,255,255,0.18)" : "transparent",
                    color: isActive ? "#fff" : "rgba(255,255,255,0.75)",
                  })}
                >
                  <Icon path={item.icon} className="w-5 h-5 flex-shrink-0" width={1.8} />
                  <span className="flex-1 text-left">{item.label}</span>
                </NavLink>
              );
            }

            const meta = ADMIN_NAV_GROUPS[row.group] || { label: row.group, icon: ICONS.dashboard };
            const isChildActive = row.children.some((c) => location.pathname.startsWith(c.to));
            const isOpen = isChildActive || openGroups.has(row.group);

            return (
              <div key={row.group}>
                <button
                  type="button"
                  onClick={() => toggleGroup(row.group)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: isChildActive ? "rgba(255,255,255,0.1)" : "transparent",
                    color: isChildActive ? "#fff" : "rgba(255,255,255,0.75)",
                  }}
                >
                  <Icon path={meta.icon} className="w-5 h-5 flex-shrink-0" width={1.8} />
                  <span className="flex-1 text-left">{meta.label}</span>
                  <Icon
                    path={ICONS.chevron}
                    className={`w-4 h-4 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    width={2}
                  />
                </button>

                {isOpen && (
                  <div className="mt-1 ml-4 pl-3 border-l border-white/10 space-y-1">
                    {row.children.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        onClick={() => setSidebarOpen(false)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                        style={({ isActive }) => ({
                          backgroundColor: isActive ? "rgba(255,255,255,0.18)" : "transparent",
                          color: isActive ? "#fff" : "rgba(255,255,255,0.75)",
                        })}
                      >
                        <Icon path={item.icon} className="w-4 h-4 flex-shrink-0" width={1.8} />
                        <span className="flex-1 text-left">{item.label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors"
            style={{ backgroundColor: "rgba(239,68,68,0.15)", color: "#fca5a5" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#ef4444";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.15)";
              e.currentTarget.style.color = "#fca5a5";
            }}
          >
            <Icon path={ICONS.logout} className="w-5 h-5" width={1.8} />
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* ══ MAIN ══ */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white/80 backdrop-blur h-16 flex items-center justify-between px-4 sm:px-6 shadow-sm sticky top-0 z-20">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden text-gray-600" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
              <Icon path={ICONS.menu} stroke={TOKENS.NAVY} width={2} />
            </button>
            <div className="min-w-0">
              <h1 className="font-bold text-base sm:text-lg truncate" style={{ color: TOKENS.NAVY }}>
                {current.label}
              </h1>
              <p className="text-xs text-gray-400 truncate">Welcome back, {user?.name}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors"
              style={{ backgroundColor: TOKENS.LIGHT, color: TOKENS.PRIMARY }}
              aria-label="Refresh"
              title="Refresh page"
            >
              <Icon path={ICONS.refresh} className="w-4 h-4" stroke={TOKENS.PRIMARY} width={2} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <div className="relative">
              <button
                className="relative text-gray-500 hover:text-blue-700 transition p-1.5 rounded-lg hover:bg-gray-100"
                aria-label="Notifications"
                aria-haspopup="true"
                aria-expanded={notifOpen}
                title={unreadCount > 0 ? `${unreadCount} unread notification(s)` : "No unread notifications"}
                onClick={openNotifications}
              >
                <Icon path={ICONS.bell} className="w-5 h-5" stroke="currentColor" width={1.8} />
                {unreadCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-bold text-white flex items-center justify-center"
                    style={{ backgroundColor: "#ef4444" }}
                  >
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>

              {notifOpen && (
                <>
                  {/* Click-away layer */}
                  <div className="fixed inset-0 z-30" onClick={() => setNotifOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-xl border border-gray-100 z-40 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-bold" style={{ color: TOKENS.NAVY }}>
                        Notifications
                      </p>
                      {unreadCount > 0 && (
                        <button
                          onClick={handleMarkAllRead}
                          disabled={markingAll}
                          className="text-[11px] font-semibold disabled:opacity-60"
                          style={{ color: TOKENS.PRIMARY }}
                        >
                          {markingAll ? "Marking…" : "Mark all read"}
                        </button>
                      )}
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                      {notifLoading ? (
                        <p className="px-4 py-8 text-center text-sm text-gray-400">Loading…</p>
                      ) : notifRows.length === 0 ? (
                        <div className="px-4 py-8 flex flex-col items-center text-center">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-2" style={{ backgroundColor: TOKENS.LIGHT }}>
                            <Icon path={ICONS.check} className="w-6 h-6" stroke={TOKENS.PRIMARY} width={1.8} />
                          </div>
                          <p className="text-sm text-gray-400">You're all caught up — no notifications.</p>
                        </div>
                      ) : (
                        notifRows.map((n) => (
                          <button
                            key={n.id}
                            onClick={() => !n.read_at && handleMarkRead(n.id)}
                            className="w-full text-left px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition"
                          >
                            <div className="flex items-start gap-2">
                              {!n.read_at && (
                                <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: TOKENS.PRIMARY }} />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
                                  {n.title}
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5 break-words">{n.body}</p>
                                <p className="text-[11px] text-gray-400 mt-1">
                                  {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                                </p>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="relative">
              <button
                onClick={() => setProfileOpen((o) => !o)}
                aria-haspopup="true"
                aria-expanded={profileOpen}
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <Avatar name={user?.name} size={36} />
                <div className="hidden sm:block text-left leading-tight">
                  <p className="text-sm font-semibold truncate max-w-[140px]" style={{ color: TOKENS.NAVY }}>
                    {user?.name}
                  </p>
                  <p className="text-[11px] text-gray-400">{role}</p>
                </div>
                <Icon
                  path={ICONS.chevron}
                  className={`w-4 h-4 transition-transform ${profileOpen ? "rotate-180" : ""}`}
                  stroke="#9ca3af"
                  width={2}
                />
              </button>

              {profileOpen && (
                <>
                  {/* Click-away layer */}
                  <div className="fixed inset-0 z-30" onClick={() => setProfileOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-64 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-xl border border-gray-100 z-40 overflow-hidden">
                    <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                      <Avatar name={user?.name} size={40} />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
                          {user?.name}
                        </p>
                        <p className="text-xs text-gray-400 truncate">{user?.email || "—"}</p>
                      </div>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold hover:bg-red-50 transition-colors"
                      style={{ color: "#dc2626" }}
                    >
                      <Icon path={ICONS.logout} className="w-4 h-4" stroke="#dc2626" width={1.8} />
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-8 lg:px-12 py-6 sm:py-8 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
